import { BookMarkInfo, BookMarkOption, brianMarker, InspectionImageCreate } from "../../_objects/inspection-image";
import { TenantDataFile } from "../../_objects/tenant-data";
import { IMAGE_FILETYPE, IMAGE_FILETYPE_MAP } from "../../app.constants";
import { InspectionFileValidator } from "./inspection-file.validator";

export class FileList {
  file: File;
  fileName: string;
  fullPath: string;
  fileStatus: string;
  locationId: string;

  constructor(file: File, fileName: string, fullPath: string, fileStatus: string) {
    this.file = file;
    this.fileName = fileName;
    this.fullPath = fullPath;
    this.fileStatus = fileStatus;
  }
}

export class ImageCreationMapper {
  public static async toImagesCreation(files: File[], inspectionId: string, locationName: string): Promise<InspectionImageCreate[]> {
    const images: InspectionImageCreate[] = [];

    for (const file of files) {
      if (
        InspectionFileValidator.isXMLFile(file.type) ||
        InspectionFileValidator.isValidJsonFile(file.name) ||
        InspectionFileValidator.isWavAudioFile(file.name)
      ) {
        continue;
      }

      if (InspectionFileValidator.isValidMediaFileType(file.type, file.name)) {
        const { wav, bookMarkOption } = await this.extractAudioAndBookMarks(files, file);

        const imageCreate = this.createModel(file, file.name, locationName, inspectionId, bookMarkOption, wav);
        images.push(imageCreate);
      }
    }

    return this.removeJsonAndAudioFiles(images);
  }

  public static async toImagesCreationFromFileLists(fileLists: FileList[], inspectionId: string) {
    const images: InspectionImageCreate[] = [];

    const files = fileLists.map((f) => f.file);

    for (const fileList of fileLists) {
      const file = fileList.file;

      if (
        InspectionFileValidator.isXMLFile(file.type) ||
        InspectionFileValidator.isValidJsonFile(file.name) ||
        InspectionFileValidator.isWavAudioFile(file.name)
      ) {
        continue;
      }

      if (InspectionFileValidator.isValidMediaFileType(file.type, file.name)) {
        const { wav, bookMarkOption } = await this.extractAudioAndBookMarks(files, file);

        const imageCreate = this.createModel(file, fileList.fileName, fileList.locationId, inspectionId, bookMarkOption, wav);
        images.push(imageCreate);
      }
    }

    return this.removeJsonAndAudioFiles(images);
  }

  public static toImageCreationFromTenantDataFile(tenantDataFile: TenantDataFile, inspectionId: string): InspectionImageCreate {
    const createInfo = new InspectionImageCreate();
    createInfo.fileName = tenantDataFile.name;
    if (tenantDataFile.name) {
      const filePath = tenantDataFile.name.split("/").reverse();
      if (filePath.length >= 2) {
        createInfo.folderName = filePath[1];
      }
    }
    createInfo.locationName = null;
    createInfo.inspectionId = inspectionId;

    createInfo.mimeType = IMAGE_FILETYPE_MAP[tenantDataFile.fileExtension.toLowerCase()];

    createInfo.deviceFileUri = tenantDataFile.downloadUrl;
    createInfo.file = new File([], tenantDataFile.name);
    return createInfo;
  }

  private static removeJsonAndAudioFiles(images: InspectionImageCreate[]) {
    return images.filter((i) => !InspectionFileValidator.isValidJsonFile(i.fileName) && !InspectionFileValidator.isWavAudioFile(i.fileName));
  }

  private static createModel(
    file: File,
    fileName: string,
    locationIdentifier: string,
    inspectionId: string,
    bookMarkOptions?: BookMarkOption[],
    audioFiles?: File[]
  ) {
    const createInfo = new InspectionImageCreate();
    createInfo.fileName = file.name;
    if (fileName) {
      const filePath = fileName.split("/").reverse();
      if (filePath.length >= 2) {
        createInfo.folderName = filePath[1];
      }
    }
    createInfo.locationName = locationIdentifier;
    createInfo.inspectionId = inspectionId;

    const extension = file.name.split(".").pop().toUpperCase();
    if (file.type === "") {
      switch (extension) {
        case IMAGE_FILETYPE.EXTENSION_HEIC.toUpperCase():
          createInfo.mimeType = IMAGE_FILETYPE.HEIC;
          break;
        case IMAGE_FILETYPE.EXTENSION_HEIF.toUpperCase():
          createInfo.mimeType = IMAGE_FILETYPE.HEIF;
          break;
        case IMAGE_FILETYPE.EXTENSION_AVI.toUpperCase():
          createInfo.mimeType = IMAGE_FILETYPE.AVI;
          break;
      }
    } else {
      createInfo.mimeType = file.type;
    }

    if (bookMarkOptions) {
      createInfo.bookMarkOption = bookMarkOptions;
    }
    const filePathValues = fileName.split("/").reverse();

    const audioFileForImage = audioFiles?.find((audioFile) => audioFile.name.split(".")[0] == filePathValues[0].split(".")[0]);
    createInfo.isExistsAudio = audioFileForImage ? true : false;
    createInfo.file = file;
    createInfo.audioFile = audioFileForImage;

    return createInfo;
  }

  private static removeMimeType(fileName: string): string {
    return fileName.split(".")[0];
  }

  private static async extractAudioAndBookMarks(files: File[], file: File) {
    let bookMarkOption: BookMarkOption[] = null;

    if (file.name.toLowerCase().endsWith(IMAGE_FILETYPE.EXTENSION_MP4)) {
      const json = files.find((f) => {
        const sameName = this.removeMimeType(file.name) === this.removeMimeType(f.name);
        const isJson = InspectionFileValidator.isValidJsonFile(f.name);
        return sameName && isJson;
      });
      if (json) {
        try {
          const jsonData: BookMarkInfo = await readJsonFile(json);

          if (jsonData.markers) bookMarkOption = this.extractFromMarkers(jsonData.markers);

          if (jsonData.EVT) bookMarkOption = this.extractFromBrianMarkers(jsonData.EVT);
        } catch (error) {
          console.error("Failed to retrieve Bookmark data", error);
        }
      }
    }

    const wav = files.filter(
      (file) => this.removeMimeType(file.name) === this.removeMimeType(file.name) && InspectionFileValidator.isWavAudioFile(file.name)
    );

    return { wav, bookMarkOption };
  }

  public static extractFromMarkers(markers: any[]): BookMarkOption[] {
    return markers.map((marker) => {
      const bookmarkOption = new BookMarkOption();
      bookmarkOption.mtime = marker.mtime.toString();
      return bookmarkOption;
    });
  }

  public static extractFromBrianMarkers(events: brianMarker[]): BookMarkOption[] {
    return events
      .filter((event) => event.TAG === "IDX")
      .map((event) => {
        const bookmarkOption = new BookMarkOption();
        bookmarkOption.mtime = event.TIM;
        return bookmarkOption;
      });
  }
}

async function readJsonFile<T>(file: File): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const jsonData = event.target.result as string;
        const result = JSON.parse(jsonData) as T;
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    reader.readAsText(file);
  });
}
